import { WebAPIConfig } from './../authentication/aws.service';
import { BucketingStrategy } from './../bucket/bucketing-strategy';
import { AppConfig } from './../app.config';
import { DashboardMetricBarConfigurator } from '../dashboard/metric/dashboard-metric-bar-configurator';
import {
    DashboardMetricCategory,
    UserProfileALMManager,
    UserProfile,
    DashboardMetricValue,
    WebApiEndpoints,
    ALM_METRIC_CONFIGS,
    ALMMetricConfig,
    numberToString,
    ALMBreakDownData,
    ALMBreakDownConfig,
    GetALMBreakDownQueryDataRequest,
    GetALMBreakDownQueryDataResult,
    convertFromCamelToHuman,
    ALMMetricValueRollupType,
    ALMLadderingType,
    CurrencyUtil,
    compareTenors,
    ALMLadderStructureType,
} from '@liquitics/transaction';
import { DashboardMetricSet } from '../dashboard/dashboard-metric';
import { AwsUtil } from '../authentication/aws.service';
import { BaseDashboardMetricBarConfigurator } from '../risk-manager/risk-metric-functions';
import { MetricTile } from '../metrics/metric-tile';
import { TreeNode } from 'primeng/api';

export interface ALMDashboardMetricData {
    currency: string;
    category: string;
    metricName: string;
    batchIdentifier: string;
    amount: number;
    // breakDownData: ALMBreakDownData[];
    errored: boolean;
}

export function aggregateBreakDownValues(
    breakDownData: ALMBreakDownData[],
    numeratorField: string,
    denominatorField: string,
    rollupType: ALMMetricValueRollupType): number[] {
    return breakDownData
        .reduce((sums: number[], data: ALMBreakDownData) => {
            if ((data[numeratorField] || data[numeratorField] === 0) && !isNaN(data[numeratorField])
                && (data[denominatorField] || data[denominatorField] === 0) && !isNaN(data[denominatorField])) {
                if (rollupType === 'COUNT' || rollupType === 'WEIGHTED') {
                    sums[0] += data[numeratorField];
                    sums[1] += data[denominatorField];
                } else {
                    // sums[0] += CurrencyUtil.roundAndScale(data[numeratorField], 2, CurrencyUtil.currencyScalePower(data.currency));
                    // sums[1] += CurrencyUtil.roundAndScale(data[denominatorField], 2, CurrencyUtil.currencyScalePower(data.currency));
                    sums[0] += CurrencyUtil.roundAndScale(data[numeratorField], 2, 1_000_000);
                    sums[1] += CurrencyUtil.roundAndScale(data[denominatorField], 2, 1_000_000);
                }

            }
            return sums;
        }, [0, 0]);
}

function getIconName(category: string): string {
    if (category === 'BALANCE_SHEET') {
        return 'library_books';
    } else if (category === 'INTEREST_BEARING') {
        return 'share';
    } else if (category === 'REGULATORY') {
        return 'account_balance';
    }
}

function addEmptyBucketData(breakDownData: ALMBreakDownData[], currency, almBucketing: BucketingStrategy) {
    almBucketing.getAllBucketTenors()
        .forEach((bucketTenor: string) => {
            const entry = {
                ccy: currency,
                almTenor: bucketTenor,
                bd1: 'NONE',
                bd2: 'NONE',
                bd3: 'NONE',
                bd4: 'NONE',
                ln1: 0.0,
                ld1: 1,
                ln2: 0.0,
                ld2: 1,
                ln3: 0.0,
                ld3: 1,
                ln4: 0.0,
                ld4: 1,
                ln5: 0.0,
                ld5: 1,
                ln6: 0.0,
                ld6: 1,
                ln7: 0.0,
                ld7: 1,
                ln8: 0.0,
                ld8: 1,
            } as ALMBreakDownData;
            entry['almTenorBucket'] = almBucketing.getBucketNameFromTenor(bucketTenor);
            breakDownData.push(entry);
        });
}

export function walkAndBuildTreeNode(
    rollupTypes: ALMMetricValueRollupType[],
    parentNode: TreeNode,
    breakDownData: ALMBreakDownData[],
    numeratorFieldNames: string[],
    denominatorFieldNames: string[],
    levels: string[],
    bucketLevel: number,
    currency: string,
    almBucketing?: BucketingStrategy) {
    if (levels.length === 0 || !breakDownData) {
        return;
    }
    if (bucketLevel === 0) {
        addEmptyBucketData(breakDownData, currency, almBucketing);
        breakDownData = sortBreakDownDataByBuckets(breakDownData);
    }
    const groupedByKey = groupBreakDownData(breakDownData, levels[0]);
    let childNodes = Object.keys(groupedByKey)
        .map((bd: string, index: number) => {
            const childData = groupedByKey[bd];
            const amounts = [];
            const deltas = [];
            for (let i = 0; i < rollupTypes.length; i++) {
                const aggregates = aggregateBreakDownValues(childData, numeratorFieldNames[i], denominatorFieldNames[i], rollupTypes[i]);
                amounts.push(getMetricAmount(aggregates, rollupTypes[i]));
                const deltaAggregates = aggregateBreakDownValues(childData, numeratorFieldNames[i] + '_delta', denominatorFieldNames[i] + '_delta', rollupTypes[i]);
                deltas.push(getMetricAmount(deltaAggregates, rollupTypes[i]));
            }
            const node = {
                label: bd,
                data: {
                    index: index,
                    level: (5 - levels.length),
                    breakDown: bd,
                    breakDownData: childData,
                    amounts: amounts,
                    deltas: deltas,
                },
                parent: parentNode,
                expanded: (parentNode.parent === undefined),
            } as TreeNode;
            for (let i = 0; i < rollupTypes.length; i++) {
                node['amount' + i] = amounts[i];
                node['delta' + i] = deltas[i];
            }
            walkAndBuildTreeNode(rollupTypes, node, childData, numeratorFieldNames, denominatorFieldNames, [...levels].splice(1), bucketLevel - 1, currency, almBucketing);
            return node;
        });
    if (levels[0] !== 'almTenorBucket') {
        // If the level we are on are the tenor buckets, we have already sorted.
        // Otherwise, do a natural sorting based on the label name so strings are consistantly sorted.
        childNodes = childNodes
            .sort((node1: TreeNode, node2: TreeNode) => {
                return node1.label.localeCompare(node2.label);
            });
    }
    parentNode.children = childNodes;
}

export function getMetricAmount(aggregates, rollupType: ALMMetricValueRollupType): number {
    if (rollupType === 'WEIGHTED') {
        return aggregates[0] / aggregates[1];
    } else {
        return aggregates[0];
    }
}

export function groupBreakDownData(breakDownData: ALMBreakDownData[], keyName: string): { [key: string]: ALMBreakDownData[] } {
    return breakDownData
        .reduce((grouped, data: ALMBreakDownData) => {
            const key = data[keyName];
            if (!key) {
                // Prevent undefined nodes
                return grouped;
            }
            grouped[key] = grouped[key] || [];
            grouped[key].push(data);
            return grouped;
        }, {});
}

export function transposeDataToTenors(breakDownData: ALMBreakDownData[],
    numeratorFieldName: string,
    denominatorFieldName: string,
    tenors: string[],
    tenorLadderingType: ALMLadderingType) {

    let tenorsToUse = tenors;
    if (tenorLadderingType === 'INVERSE_CUMULATIVE') {
        // We want to interate in reverse if inverrse cumulative
        tenorsToUse = tenors.slice().reverse();
    }
    breakDownData.forEach((bdd: ALMBreakDownData) => {
        let runningNumeratorValue = 0.0;
        tenorsToUse.forEach((tenor: string, tenorIndex: number) => {
            let bddNumeratorValue = 0.0;
            if (tenor === bdd['almTenorBucket']) {
                bddNumeratorValue = bdd[numeratorFieldName];
            }
            let tenorSuffix;
            if (tenorLadderingType === 'BALANCE') {
                runningNumeratorValue = bddNumeratorValue;
                tenorSuffix = tenor;
            } else if (tenorLadderingType === 'CUMULATIVE' || tenorLadderingType === 'INVERSE_CUMULATIVE') {
                runningNumeratorValue += bddNumeratorValue;
                tenorSuffix = tenor;
            }
            bdd[numeratorFieldName + tenorSuffix] = runningNumeratorValue;
            bdd[denominatorFieldName + tenorSuffix] = bdd[denominatorFieldName];
            bdd[numeratorFieldName + tenorSuffix + '_delta'] = bddNumeratorValue;
            bdd[denominatorFieldName + tenorSuffix + '_delta'] = bdd[denominatorFieldName];
        });
    });
}

export function stampBreakDownDataWithTenorBuckets(breakDownData: ALMBreakDownData[], almBucketing: BucketingStrategy): ALMBreakDownData[] {
    breakDownData = breakDownData
        .filter((bdd: ALMBreakDownData) => {
            return (bdd.almTenor && bdd.almTenor.length > 0);
        });
    // Restamp the tenors with the Bucketing tenors
    breakDownData = breakDownData
        .map((bdd: any) => {
            bdd['almTenorBucket'] = almBucketing.getBucketNameFromTenor(bdd.almTenor);
            return bdd;
        })
        .filter((bdd: any) => {
            // Since the bucketing stategy will return undefined for unbucketable items (items out of range),
            // we can check to see if it got set, and effectively filter the undesirable results out.
            // For the most part this 'should' be done server side, but this also catches undesired entries.
            return bdd['almTenorBucket'];
        });
    breakDownData = sortBreakDownDataByBuckets(breakDownData);
    return breakDownData;
}

function sortBreakDownDataByBuckets(breakDownData: ALMBreakDownData[]): ALMBreakDownData[] {
    return breakDownData
        .sort((a: ALMBreakDownData, b: ALMBreakDownData) => {
            // Need a better solution for this eventually
            let aBucket = a['almTenorBucket'];
            if (aBucket === 'Longterm') {
                aBucket = '50Y';
            }
            let bBucket = b['almTenorBucket'];
            if (bBucket === 'Longterm') {
                bBucket = '50Y';
            }
            return compareTenors(aBucket, bBucket);
        });
}

export function cleanUpBucketedNodes(nodes: TreeNode[], bucketLevel: number, currentLevel: number): TreeNode[] {
    let newNodes: TreeNode[];
    if (bucketLevel === currentLevel) {
        if (nodes.length === 1) {
            newNodes = nodes;
        } else {
            newNodes = nodes
                .filter((node: TreeNode) => {

                })
        }
    }
    return newNodes;
}

export class ALMDashboardMetricBarConfigurator extends BaseDashboardMetricBarConfigurator<ALMDashboardMetricData>
    implements DashboardMetricBarConfigurator<ALMDashboardMetricData> {
    currency: string;
    stress: string;
    batchIdentifier: string;
    dashboardMetricCategory: DashboardMetricCategory;
    profile: UserProfileALMManager;
    calculation: string;
    constructor() {
        super(4);
    }

    init(
        selectedCurrency: string,
        selectedSubType: string,
        selectedBatchIdentifier: string,
        selectedDashboardMetricCategory: DashboardMetricCategory,
        profile: UserProfile,
        selectedCalculation: string
    ) {
        this.currency = selectedCurrency;
        this.stress = selectedSubType;
        this.batchIdentifier = selectedBatchIdentifier;
        this.dashboardMetricCategory = selectedDashboardMetricCategory;
        this.profile = profile as UserProfileALMManager;
        this.calculation = selectedCalculation;
    }

    private findMetricValue(metricValues: DashboardMetricValue[], type: string, currency: string,
        calculation: string): DashboardMetricValue {
        return metricValues.find((metricValue: DashboardMetricValue) => {
            return metricValue.type === type && metricValue.currency === currency && metricValue.calculation === calculation;
        })
    }


    queryDashboardData(awsUtil: AwsUtil, appConfig: AppConfig): Promise<ALMDashboardMetricData>[] {
        // const completedOperatingDate = appConfig.getTodayStartUTC().subtract(1, 'day').format('YYYY-MM-DD');
        // const promises: Promise<GetALMBreakDownQueryDataResult>[] = [];
        return ALM_METRIC_CONFIGS
            .filter((metricConfig: ALMMetricConfig) => {
                return metricConfig.code !== 'FTP';
            })
            .map(async (metricConfig: ALMMetricConfig) => {
                const breakDownConfig: ALMBreakDownConfig = metricConfig.breakDownConfigs[0];
                try {
                    const result: GetALMBreakDownQueryDataResult = await awsUtil.webAPICall<GetALMBreakDownQueryDataResult>(
                        {
                            apiPath: WebApiEndpoints.QueryALMMetricData,
                            retryConfig: {
                                retryAttempts: 2
                            }
                        } as WebAPIConfig,
                        {
                            type: 'METRIC',
                            scenarioType: 'BASE',
                            currency: this.currency,
                            batchIdentifier: this.batchIdentifier,
                            breakDownQueryConfigName: metricConfig.name,
                            breakDown: breakDownConfig.name,
                        } as GetALMBreakDownQueryDataRequest);
                    const metricData = {
                        currency: this.currency,
                        category: metricConfig.category,
                        metricName: metricConfig.name,
                        // breakDownData: result.breakDownData,
                        batchIdentifier: this.batchIdentifier,
                        amount: this.computeAndGetMetricAmount(result.breakDownData, metricConfig),
                    } as ALMDashboardMetricData;
                    if (result['error']) {
                        metricData.errored = true;
                    }
                    return metricData;
                } catch (err) {
                    console.log(err);
                    return {
                        currency: this.currency,
                        category: metricConfig.category,
                        metricName: metricConfig.name,
                        batchIdentifier: this.batchIdentifier,
                        errored: true,
                    } as ALMDashboardMetricData;
                }
            })
    }

    computeAndGetMetricAmount(breakDownData: ALMBreakDownData[], metricConfig: ALMMetricConfig): number {
        if (!breakDownData) {
            return NaN;
        }
        const aggregates = aggregateBreakDownValues(breakDownData, 'mn', 'md', metricConfig.metricRollupType);
        if (metricConfig.metricRollupType === 'WEIGHTED') {
            return aggregates[0] / aggregates[1];
        } else {
            return aggregates[0];
        }
    }

    generateDashboardMetricsSets(almMetricData: ALMDashboardMetricData[]): DashboardMetricSet[] {
        const metricSets: DashboardMetricSet[] = [];
        let lastCategory;
        let categoryIndex = 0;
        let placeholderCount = 0;
        almMetricData.forEach((data: ALMDashboardMetricData, index: number) => {
            let value;
            if (data.errored) {
                value = '!!';
            } else {
                value = data.amount ? numberToString(data.amount, '0.00a') : '--';
            }
            const metricSet = {
                type: 'Single',
                initialized: true,
                icon: getIconName(data.category),
                fontSize: '3em',
                label: convertFromCamelToHuman(data.metricName),
                name: data.metricName,
                // metrics: '',
                values: [value],
                // dates: '',
                // subType: '',
                // category: '',
                // categoryIndex: '',
                underlyingData: data,
            } as DashboardMetricSet;

            if (lastCategory && lastCategory !== data.category) {
                const remainderCols = (index + placeholderCount) % this.maxColumnsPerRow;
                if (remainderCols > 0) {
                    for (let i = 0; i < this.maxColumnsPerRow - remainderCols; i++) {
                        metricSets.push({ type: 'Placeholder' } as DashboardMetricSet);
                        placeholderCount++;
                    }
                }
                categoryIndex++;
            }
            metricSet.categoryIndex = categoryIndex;
            metricSets.push(metricSet);
            lastCategory = data.category;
        });
        return metricSets;
    }

    initLabelTiles(dashboardMetricSetData: DashboardMetricSet[]): MetricTile<DashboardMetricSet>[] {
        const labelTiles: MetricTile<DashboardMetricSet>[] = [];
        return labelTiles;
    }
}
